/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { IAddFamilyInformationFacade } from "libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/add-family-information-facade-interface";
import { Injectable } from "@angular/core";
import {
  convertCableTrayStringToSysqueSizeRangeVM,
  convertCableTraySysqueSizeRangeVMToString,
  convertConduitStringToSysqueSizeRangeVM,
  convertConduitSysqueSizeRangeVMToString,
  convertFeetToMm,
  convertFieldFabStringToSysqueSizeRangeVM,
  convertHandAndMachineBendStringToSysqueSizeRangeVM,
  convertSysqueSizeRangeItemsVMToFieldFab,
  convertSysqueSizeRangeVMToHandAndMachineBendString,
  getCableTrayConnectors,
  getConduitConnectors,
  getJunctionBoxConnectors,
  applyChangesForLineGraphics,
  applyDeleteServiceTypeChanges,
  applyCreateServiceTypeChanges,
  convertKickHandAndMachineBendStringToSysqueSizeRangeVM,
  convertSysqueSizeRangeItemsVMToKickHandAndMachineBendString,
  getUserCustomContentConnectors,
  getDeviceEquipmentConnectors,
  convertIntARGBToRGBHex,
  convertRGBHexToIntARGB,
  convertRGBAStringToIntARGB,
  setItem,
} from '../../../../../utils/src/lib/family-information.utils';
import { SysqueSizeRangeItemsVM } from "libs/sysque/shared/ui/src/lib/models/sysque-size-range-vm";
import { LocalStorageService } from "../../../../../utils/src/lib/local-storage/local-storage.service";
import { GlobalConstants } from "../../constants/global-constants";
import { FieldFabConduitSizeRangeVM, HandAndMachineBendSizeRangeVM, IFamilyInformationItem, KickHandAndMachineBendSizeRangeVM, Parameters } from "../../components/add-family-information/add-family-information-types";
import { AddFamilyInformationData, AddFamilyInformationReturnCodes, FamilyInformationItem, ServiceType, connectorDescription } from "../../models/family-information-model";
import { Store } from "@ngrx/store";
import { ApplyDeviceEquipmentDetails, ChangeFamilyInformationDialogDimensions, DeleteServiceType, GetPdfLocationPath, GetUserCustomImage, OpenPdfFile, SaveFamilyInformationInXml, SaveServiceType, SendClosePopupAction, ValidateAndGetData } from "./add-family-information-actions";
import { from, Observable } from "rxjs";
import { selectDeviceEquipmentInputData, selectOpenPdfFile, selectPdfLocationPath, selectUserCustomImage } from "./add-family-information-selector";
import { AddFamilyInformationState } from "./add-family-information-reducer";
import { sendPopupRoutingAction } from '../toolbelt.actions';
import { SysquePopupConfig } from '../../models/sysque-popup-config';
import { createDialog } from '../../popup-dialog-factory/popup-dialog.facade';
import { FamilyInformationReceiveController } from '../../receive-controllers/family-information-receive-controller';
import { ReceiveMessageHandler } from '@mep/web-browser-bridge';
import { Item } from '../../../../../../toolbelt/data-access/src/lib/domain/item';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';
import { BackendService } from "../../../../../../toolbelt/utils/src/lib/sysque-backend/backend.service";
import { LineGraphicsDialogConfig } from '../../../../../feature/src/lib/components/add-family-information/line-graphics';
import { CreateServiceTypeDialogConfig } from "../../components/add-family-information/create-service-type";
import { DeviceEquipmentDetailsDataToApply, DeviceEquipmentDetailsInputData } from "../../models/device-equipment-details-return-codes";
import { TooltipItemInfo } from "../../../../../utils/src/lib/common-helper/tooltip-item-info/tooltip-item-info";
import { SysQueStateService } from '../../../../../utils/src/lib/sysque-electrical-state/sysque-elecrical-state.service';

@Injectable({ providedIn: 'root' })
export class AddFamilyInformationFacade implements IAddFamilyInformationFacade {
  public fittingList: Item[] = [];
  public state: SysqueElectricalState = new SysqueElectricalState();
  constructor(
    private store: Store<AddFamilyInformationState>,
    private readonly receiveMessageHandler: ReceiveMessageHandler,
    private localStorageService: LocalStorageService,
    private backendService: BackendService,
    private sysqueStateService: SysQueStateService
  ) {}

  public getHangerSetUpStatus(): boolean {
    return this.localStorageService.getStorage(GlobalConstants.HangerCopySetUp);
  }

  public getLocalStorageData(key: string): IFamilyInformationItem[] | SysqueElectricalState {
    return this.localStorageService.getStorage(key);
  }  

  public registerControllers(): void {
    this.receiveMessageHandler.registerController(new FamilyInformationReceiveController(this.localStorageService));
  }

  public setItemToAdd(item: AddFamilyInformationData): IFamilyInformationItem {
    return setItem(item);
  }

  public convertCableTrayStringToSysqueSizeRangeVM(data: string): SysqueSizeRangeItemsVM[] {
    return convertCableTrayStringToSysqueSizeRangeVM(data);
  }

  public convertCableTraySysqueSizeRangeVMToString(data: SysqueSizeRangeItemsVM[]): string {
    return convertCableTraySysqueSizeRangeVMToString(data);
  }

  public convertConduitStringToSysqueSizeRangeVM(data: string): SysqueSizeRangeItemsVM[] {
    return convertConduitStringToSysqueSizeRangeVM(data);
  }

  public convertConduitSysqueSizeRangeVMToString(data: SysqueSizeRangeItemsVM[]): string {
    return convertConduitSysqueSizeRangeVMToString(data);
  }

  public convertFieldFabStringToSysqueSizeRangeVM(data: FieldFabConduitSizeRangeVM): SysqueSizeRangeItemsVM[] {
    return convertFieldFabStringToSysqueSizeRangeVM(data);
  }

  public convertSysqueSizeRangeItemsVMToFieldFab(data: SysqueSizeRangeItemsVM[]): FieldFabConduitSizeRangeVM {
    return convertSysqueSizeRangeItemsVMToFieldFab(data);
  }

  public convertHandAndMachineBendStringToSysqueSizeRangeVM(
    data: HandAndMachineBendSizeRangeVM,
    offsetType: string
  ): SysqueSizeRangeItemsVM[] {
    return convertHandAndMachineBendStringToSysqueSizeRangeVM(data, offsetType);
  }

  public convertSysqueSizeRangeVMToHandAndMachineBendString(
    data: SysqueSizeRangeItemsVM[],
    offsetType: string
  ): HandAndMachineBendSizeRangeVM {
    return convertSysqueSizeRangeVMToHandAndMachineBendString(data, offsetType);
  }

  public convertKickHandAndMachineBendStringToSysqueSizeRangeVM(
    data: KickHandAndMachineBendSizeRangeVM
  ): SysqueSizeRangeItemsVM[] {
    return convertKickHandAndMachineBendStringToSysqueSizeRangeVM(data);
  }

  public convertSysqueSizeRangeItemsVMToKickHandAndMachineBendString(
    data: SysqueSizeRangeItemsVM[]
  ): KickHandAndMachineBendSizeRangeVM {
    return convertSysqueSizeRangeItemsVMToKickHandAndMachineBendString(data);
  }

  public convertFeetToMm(feet: number): number {
    return convertFeetToMm(feet);
  }

  public convertMmToFeet(mm: number): number {
    return mm / 304.8;
  }

  public getFamilyInformationItem(): IFamilyInformationItem {
    return this.localStorageService.getStorage(GlobalConstants.FAMILY_INFORMATION_ITEM) as IFamilyInformationItem;
  }

  public getFamilyInformationResponse(): FamilyInformationItem {
    return this.localStorageService.getStorage(GlobalConstants.FAMILY_INFORMATION_RESPONSE) as FamilyInformationItem;
  }

  public updateItem(item: IFamilyInformationItem): void {
    this.state = this.localStorageService.getStorage(
      GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY
    ) as SysqueElectricalState;

    if (item.isFromEdit) {
      // Define the predicate function based on the item.isFromEdit flag
      let predicate = item.isFromEdit
        ? (element: Item) => element.id === item.id && element.name === item.name
        : (element: Item) => element.id === item.id;

      let index = this.state.fittingList.findIndex(predicate);

      // If the item is not found, exit the function
      if (index === -1) {
        return;
      }

      this.state.fittingList[index] = item;
      this.localStorageService.updateDispatchLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.state);
      this.localStorageService.updateDispatchLocalStorage(GlobalConstants.UPDATE_PANEL_TYPE, item.panelCircuitChecked)
      this.sysqueStateService.setElectricalState(this.state);
    } else {
      this.updateItemsList(item);
    }
  }

  public getCableTrayConnectors(): connectorDescription[] {
    return getCableTrayConnectors();
  }

  public getConduitConnectors(): connectorDescription[] {
    return getConduitConnectors();
  }

  public getJunctionBoxConnectors(): connectorDescription[] {
    return getJunctionBoxConnectors();
  }

  public getUserCustomContentConnectors(): connectorDescription[] {
    return getUserCustomContentConnectors();
  }

  public getDeviceEquipmentConnectors(): connectorDescription[] {
    return getDeviceEquipmentConnectors();
  }

  public convertIntARGBToRGBHex(argb: number): string {
    return convertIntARGBToRGBHex(argb);
  }

  public convertRGBHexToIntARGB(hex: string): number | null {
    return convertRGBHexToIntARGB(hex);
  }

  public convertRGBAStringToIntARGB(color: string): number {
    return convertRGBAStringToIntARGB(color);
  }

  public saveFamilyInformationInXml(item: IFamilyInformationItem): void {
    this.store.dispatch(SaveFamilyInformationInXml({ item: item }));
  }

  public windowResize(height: number): void {
    this.store.dispatch(ChangeFamilyInformationDialogDimensions({ height: height }));
  }

  public sendClosePopupAction(): void {
    this.store.dispatch(SendClosePopupAction());
  }

  public getUpdatedProjectParameters(): Parameters {
    return this.localStorageService.getStorage(GlobalConstants.PROJECT_PARAMS) as Parameters;
  }

  public onClickedToOpenProjectData(): void {
   let sysquePopupConfig = new SysquePopupConfig();
    createDialog(sysquePopupConfig, GlobalConstants.PROJECT_DATA_DIALOG);
    this.store.dispatch(
      sendPopupRoutingAction({
        route: GlobalConstants.PROJECT_DATA_DIALOG,
        width: sysquePopupConfig.width,
        height: sysquePopupConfig.height,
      })
    );
  }

  public updateLocalStorage(key: string, value: any): void {
    this.localStorageService.updateDispatchLocalStorage(key, value);
  }

  public getPdfLocationPath(): Observable<string> {
    this.store.dispatch(GetPdfLocationPath());
    return this.store.select(selectPdfLocationPath);
  }

  public openPdfFile(path: string): Observable<AddFamilyInformationReturnCodes> {
    this.store.dispatch(OpenPdfFile({ filePath: path }));
    return this.store.select(selectOpenPdfFile);
  }

  public getPatterns(): Observable<string[]> {
    return from<Promise<string[]>>(this.backendService.messageHandler.sendRequestAsync(GlobalConstants.GetPatterns));
  }

  public applyChangesForLineGraphics(
    item: IFamilyInformationItem,
    lineGraphicsDialogConfig: LineGraphicsDialogConfig
  ): IFamilyInformationItem {
    return applyChangesForLineGraphics(item, lineGraphicsDialogConfig);
  }

  public saveServiceType(serviceType: ServiceType): void {
    this.store.dispatch(SaveServiceType({ serviceTypeToSave: serviceType }));
  }

  public applyCreateServiceTypeChanges(
    item: IFamilyInformationItem,
    createServiceTypeDialogConfig: CreateServiceTypeDialogConfig
  ): ServiceType {
    return applyCreateServiceTypeChanges(item, createServiceTypeDialogConfig);
  }

  public deleteServiceType(serviceType: ServiceType): void {
    this.store.dispatch(DeleteServiceType({ serviceTypeToDelete: serviceType }));
  }

  public applyDeleteServiceTypeChanges(item: IFamilyInformationItem): boolean {
    return applyDeleteServiceTypeChanges(item);
  }

  public getUserCustomImage(systemName: string, name: string): Observable<string> {
    this.store.dispatch(GetUserCustomImage({ systemName: systemName, name: name }));
    return this.store.select(selectUserCustomImage);
  }

  public validateAndGetData(): Observable<DeviceEquipmentDetailsInputData> {
    this.store.dispatch(ValidateAndGetData());
    return this.store.select(selectDeviceEquipmentInputData);
  }

  public applyDeviceEquipmentDetails(item: DeviceEquipmentDetailsDataToApply): void {
    this.store.dispatch(ApplyDeviceEquipmentDetails({ item: item }));
  }

  private async updateItemsList(item: Item): Promise<void> {
    this.fittingList = await this.getFittings(this.state.selectedSystem, this.state.selectedCategory);
    for (let index = 0; index < this.fittingList.length; index++) {
      this.fittingList[index].tooltipItems = new TooltipItemInfo().getTooltipItemInfo(
        item,
        this.state.selectedCategory
      );
    }
    this.state.fittingList = [];
    this.fittingList.forEach((item) => {
      this.state.fittingList.push(item);
    });
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.state);
    this.sysqueStateService.setElectricalState(this.state);
  }

  private getFittings(systemName: string, categoryName: string): Promise<Item[]> {
    return this.backendService.messageHandler.sendRequestAsync(GlobalConstants.GetFittingsBySystemNameAndCategory, [
      systemName,
      categoryName,
      false,
    ]);
  } 
}